import React, { useState, useEffect } from 'react';
import { Container, Form, Button, Card, Alert, Row, Col, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import api from '../api/axios';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';

const Profile = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const { user, token, updateUser, fetchUserProfile } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();

  // Profile fields
  const [username, setUsername] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [orderConfirmationEmails, setOrderConfirmationEmails] = useState('');

  // Password change fields
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // Address fields
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [country, setCountry] = useState('');
  const [phone, setPhone] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    fetchProfile();
  }, [user, navigate]);

  const fetchProfile = async () => {
    try {
      const response = await api.get('/api/user/profile');

      const { user: userData, address } = response.data;
      
      setUsername(userData.username || '');
      setFirstName(userData.first_name || userData.firstName || '');
      setLastName(userData.last_name || userData.lastName || '');
      setEmail(userData.email || '');
      setCompany(userData.company || '');
      setOrderConfirmationEmails(userData.order_confirmation_emails || '');

      if (address) {
        setAddressLine1(address.address_line1 || '');
        setAddressLine2(address.address_line2 || '');
        setCity(address.city || '');
        setState(address.state || '');
        setPostalCode(address.postal_code || '');
        setCountry(address.country || '');
        setPhone(address.phone || '');
      }

      setLoading(false);
    } catch (err) {
      console.error('Fetch profile error:', err);
      setError(err.response?.data?.error || t('profile.errorLoad'));
      setLoading(false);
    }
  };

  const handleProfileUpdate = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);

    try {
      // Validate password fields if changing password
      if (newPassword || confirmPassword || currentPassword) {
        if (!currentPassword) {
          setError(t('profile.errorCurrentPwd'));
          setSaving(false);
          return;
        }
        if (newPassword !== confirmPassword) {
          setError(t('profile.errorPwdMismatch'));
          setSaving(false);
          return;
        }
        if (newPassword.length < 6) {
          setError(t('profile.errorPwdTooShort'));
          setSaving(false);
          return;
        }
      }

      const profileData = {
        username,
        firstName,
        lastName,
        email,
        company,
        orderConfirmationEmails,
        language
      };

      if (newPassword) {
        profileData.currentPassword = currentPassword;
        profileData.newPassword = newPassword;
      }

      await api.put('/api/user/profile', profileData);

        setSuccess(t('profile.successUpdated'));
      
      // Clear password fields
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');

        // Refresh user data from server
        await fetchUserProfile();
    } catch (err) {
      console.error('Update profile error:', err);
      setError(err.response?.data?.error || t('profile.errorLoad'));
    } finally {
      setSaving(false);
    }
  };

  const handleAddressUpdate = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);

    try {
      await api.put('/api/user/address', {
        address_line1: addressLine1,
        address_line2: addressLine2,
        city,
        state,
        postal_code: postalCode,
        country,
        phone
      });

      setSuccess(t('profile.successAddress'));
    } catch (err) {
      console.error('Update address error:', err);
      setError(err.response?.data?.error || t('profile.errorLoad'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Container className="py-5 text-center">
        <Spinner animation="border" />
      </Container>
    );
  }

  return (
    <Container className="py-5">
      <h1 className="mb-4">{t('profile.title')}</h1>

      {error && <Alert variant="danger" dismissible onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert variant="success" dismissible onClose={() => setSuccess('')}>{success}</Alert>}

      <Row>
        <Col md={6}>
          {/* Profile Information Card */}
          <Card className="mb-4">
            <Card.Header as="h5">{t('profile.accountInfo')}</Card.Header>
            <Card.Body>
              <Form onSubmit={handleProfileUpdate}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('auth.username')}</Form.Label>
                  <Form.Control
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>First Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>Last Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>{t('auth.email')}</Form.Label>
                  <Form.Control
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>{t('profile.company')}</Form.Label>
                  <Form.Control
                    type="text"
                    value={company}
                    onChange={(e) => setCompany(e.target.value)}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>{t('profile.orderEmails')}</Form.Label>
                  <Form.Control
                    type="text"
                    value={orderConfirmationEmails}
                    onChange={(e) => setOrderConfirmationEmails(e.target.value)}
                    placeholder="email@example.com, second@example.com"
                  />
                  <Form.Text className="text-muted">
                    {t('profile.orderEmailsHint')}
                  </Form.Text>
                </Form.Group>

                <hr className="my-4" />

                <h6 className="mb-3">{t('profile.changePassword')}</h6>

                <Form.Group className="mb-3">
                  <Form.Label>{t('profile.currentPassword')}</Form.Label>
                  <Form.Control
                    type="password"
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder={t('profile.passwordPlaceholder')}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>{t('profile.newPassword')}</Form.Label>
                  <Form.Control
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder={t('profile.passwordPlaceholder')}
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>{t('profile.confirmPassword')}</Form.Label>
                  <Form.Control
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder={t('profile.confirmPasswordPlaceholder')}
                  />
                </Form.Group>

                <Button 
                  type="submit" 
                  variant="primary"
                  disabled={saving}
                >
                  {saving ? t('profile.saving') : t('profile.updateProfile')}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>

        <Col md={6}>
          {/* Address Information Card */}
          <Card className="mb-4">
            <Card.Header as="h5">{t('profile.shippingAddress')}</Card.Header>
            <Card.Body>
              <Form onSubmit={handleAddressUpdate}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('profile.addressLine1')}</Form.Label>
                  <Form.Control
                    type="text"
                    value={addressLine1}
                    onChange={(e) => setAddressLine1(e.target.value)}
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label>{t('profile.addressLine2')}</Form.Label>
                  <Form.Control
                    type="text"
                    value={addressLine2}
                    onChange={(e) => setAddressLine2(e.target.value)}
                  />
                </Form.Group>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>{t('auth.city')}</Form.Label>
                      <Form.Control
                        type="text"
                        value={city}
                        onChange={(e) => setCity(e.target.value)}
                        required
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>{t('profile.stateProvince')}</Form.Label>
                      <Form.Control
                        type="text"
                        value={state}
                        onChange={(e) => setState(e.target.value)}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>{t('auth.postalCode')}</Form.Label>
                      <Form.Control
                        type="text"
                        value={postalCode}
                        onChange={(e) => setPostalCode(e.target.value)}
                        required
                      />
                    </Form.Group>
                  </Col>

                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>{t('auth.country')}</Form.Label>
                      <Form.Control
                        type="text"
                        value={country}
                        onChange={(e) => setCountry(e.target.value)}
                        required
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label>{t('profile.phone')}</Form.Label>
                  <Form.Control
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                </Form.Group>

                <Button 
                  type="submit" 
                  variant="primary"
                  disabled={saving}
                >
                  {saving ? t('profile.saving') : t('profile.updateAddress')}
                </Button>
              </Form>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Profile;
